import styled from "styled-components";
import React from "react";
import { ShoppingCart, X } from "lucide-react";

import { saveOrderToLocalHistory } from "../../../../util";
import { useRoom } from "../../../../contexts/room";
import { useLanguage } from "../../../../contexts/language";
import { RoomInputModal } from "../../../../components/room-input-modal";
import { QuantityStepper } from "../../../../components/quantity-stepper";
import { PageHeader } from "../../../../components/page-header";

const Page = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
`;

const Container = styled.div`
  margin: 0 auto;
  max-width: 720px;
  padding: 16px 16px 96px;
`;

const Subtitle = styled.p`
  color: #777;
  margin: 0 0 16px;
`;

const GroupTitle = styled.h3`
  margin: 24px 0 8px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  background-color: #fff;
  padding: 16px;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Muted = styled.span`
  color: #6b7280;
  font-size: 13px;
`;

const Button = styled.button`
  border: none;
  border-radius: 8px;
  background-color: #c9a961;
  color: #fff;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SmallInput = styled.input`
  width: 64px;
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 6px;
`;

const Select = styled.select`
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 6px;
`;

const StepperField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SummaryLine = styled.div`
  margin: 10px 0 4px;
  font-size: 14px;
  color: #333;
`;

const ErrorText = styled.p`
  color: #c0392b;
  margin: 0;
`;

// Floating cart button + slide-in drawer — mirrors the FNB ordering UX (components/cart-drawer.jsx)
// so the add-on cart is not pinned to the bottom of the catalog.
const FloatingCartButton = styled.button`
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  max-width: 688px;
  margin: 0 auto;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 12px;
  background-color: #c9a961;
  color: #fff;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(201, 169, 97, 0.4);
`;

const CartIconWrapper = styled.div`
  position: relative;
  display: flex;
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background-color: #fff;
  color: #c9a961;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 6;
`;

const Drawer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 7;
  width: 100%;
  max-width: 26rem;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e5e5;
`;

const DrawerTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #333;
  padding: 4px;
  display: flex;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DrawerFooter = styled.div`
  border-top: 1px solid #e5e5e5;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CartItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #ececec;
  border-radius: 10px;
  background: #fafafa;
`;

const CartItemLabel = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 600;
`;

const CartItemPrice = styled.div`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
`;

const CartItemRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const RemoveButton = styled.button`
  border: none;
  background: none;
  color: #c0392b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const CartSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
`;

const CartTotal = styled.span`
  font-size: 16px;

  strong {
    font-size: 18px;
    color: #1a1a1a;
  }
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 10px;
  margin-top: 4px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const fmtRupiah = (value) =>
  `Rp${Math.round(Number(value || 0)).toLocaleString("id-ID")}`;

const COPY = {
  en: {
    title: "Add-On Facility",
    subtitle:
      "Extra bed, Nintendo rent, swimming ring rent — orderable to your room.",
    setRoomPrompt: "Set your room number to see the add-ons available to you.",
    setRoomBtn: "Set room number",
    loading: "Loading add-ons…",
    noneAvailable: "No add-ons available right now.",
    loadError: "Failed to load add-ons",
    createError: "Failed to create order",
    groupExtraBed: "Extra Bed",
    groupNintendo: "Nintendo Rent",
    groupSwimmingRing: "Swimming Ring Rent",
    perNight: "/ night",
    available: (n) => `${n} available`,
    nights: "Nights",
    beds: "Beds",
    date: "Date",
    hour: "Hour",
    duration: "Duration",
    consoles: "Consoles",
    selectHour: "Select",
    taken: "(taken)",
    selectHourPrompt: "Select an hour to see the price",
    soldOut: "Sold out",
    add: "Add to cart",
    deliverTo: (room) => `deliver to room ${room}`,
    nightUnit: (n) => (n > 1 ? "nights" : "night"),
    bedUnit: (n) => (n > 1 ? "beds" : "bed"),
    hourUnit: (n) => (n > 1 ? "hours" : "hour"),
    consoleUnit: (n) => (n > 1 ? "consoles" : "console"),
    remove: "Remove",
    deliverToRoom: "Deliver to room",
    total: "Total",
    emailLabel: "Email for receipt + invoice (optional)",
    orderPay: "Order & Pay online",
    processing: "Processing…",
    xenditNote:
      "You pay online (Xendit) — the exact amount is confirmed on the payment page before you pay.",
    viewCart: "View cart",
    cartTitle: "Your cart",
    emptyCart: "Your cart is empty.",
  },
  id: {
    title: "Fasilitas Tambahan",
    subtitle:
      "Tempat tidur tambahan, sewa Nintendo, sewa ban renang — bisa dipesan ke kamar Anda.",
    setRoomPrompt:
      "Atur nomor kamar Anda untuk melihat add-on yang tersedia untuk Anda.",
    setRoomBtn: "Atur nomor kamar",
    loading: "Memuat add-on…",
    noneAvailable: "Belum ada add-on yang tersedia saat ini.",
    loadError: "Gagal memuat add-on",
    createError: "Gagal membuat pesanan",
    groupExtraBed: "Tempat Tidur Tambahan",
    groupNintendo: "Sewa Nintendo",
    groupSwimmingRing: "Sewa Ban Renang",
    perNight: "/ malam",
    available: (n) => `${n} tersedia`,
    nights: "Malam",
    beds: "Bed",
    date: "Tanggal",
    hour: "Jam",
    duration: "Durasi",
    consoles: "Konsol",
    selectHour: "Pilih",
    taken: "(terisi)",
    selectHourPrompt: "Pilih jam untuk melihat harga",
    soldOut: "Habis",
    add: "Tambah ke keranjang",
    deliverTo: (room) => `antar ke kamar ${room}`,
    nightUnit: () => "malam",
    bedUnit: () => "bed",
    hourUnit: () => "jam",
    consoleUnit: () => "konsol",
    remove: "Hapus",
    deliverToRoom: "Antar ke kamar",
    total: "Total",
    emailLabel: "Email untuk struk + invoice (opsional)",
    orderPay: "Pesan & Bayar online",
    processing: "Memproses…",
    xenditNote:
      "Anda membayar online (Xendit) — jumlah pastinya dikonfirmasi di halaman pembayaran sebelum Anda membayar.",
    viewCart: "Lihat keranjang",
    cartTitle: "Keranjang Anda",
    emptyCart: "Keranjang Anda kosong.",
  },
};

const availabilitySuffix = (facility, copy) =>
  facility.available !== null && facility.available !== undefined
    ? ` · ${copy.available(facility.available)}`
    : "";

const isSoldOut = (facility) =>
  facility.available !== null &&
  facility.available !== undefined &&
  facility.available <= 0;

// Extra bed: per-night pricing. The guest picks nights (stepper) then adjusts beds (stepper) — each
// nights value is its own cart line so the total + the room it goes to are never a guess.
const ExtraBedCard = (props) => {
  const facility = props.facility;
  const copy = props.copy;
  const [nights, setNights] = React.useState(1);
  const unit = Number(facility.unit_price?.amount || 0);
  const key = `${facility.id}:${nights}`;
  const quantity = props.getQuantity(key);
  const soldOut = isSoldOut(facility);
  const payload = {
    key,
    facilityId: facility.id,
    charge_group: facility.charge_group,
    facilityName: facility.name,
    nights,
    unit,
  };

  return (
    <Card>
      <div>
        <strong>{facility.name}</strong>
        <div>
          <Muted>{facility.guest_subtitle}</Muted>
        </div>
        <div>
          <Muted>
            {facility.unit_price.formatted_text} {copy.perNight}
            {availabilitySuffix(facility, copy)}
          </Muted>
        </div>
      </div>
      <Row>
        <StepperField>
          <Muted>{copy.nights}</Muted>
          <QuantityStepper
            value={nights}
            min={1}
            onDecrease={() => setNights((current) => Math.max(1, current - 1))}
            onIncrease={() => setNights((current) => current + 1)}
          />
        </StepperField>
        <StepperField>
          <Muted>{copy.beds}</Muted>
          <QuantityStepper
            value={quantity}
            onDecrease={() => props.onSetQuantity(payload, quantity - 1)}
            onIncrease={() => props.onSetQuantity(payload, quantity + 1)}
            disabledIncrease={soldOut}
          />
        </StepperField>
      </Row>
    </Card>
  );
};

// Swimming ring: pure quantity. The stepper drives the cart directly (plus-from-0 adds the line),
// exactly like the FNB menu items.
const QuantityCard = (props) => {
  const facility = props.facility;
  const copy = props.copy;
  const key = `${facility.id}`;
  const quantity = props.getQuantity(key);
  const unit = Number(facility.unit_price?.amount || 0);
  const soldOut = isSoldOut(facility);
  const payload = {
    key,
    facilityId: facility.id,
    charge_group: facility.charge_group,
    facilityName: facility.name,
    unit,
  };

  return (
    <Card>
      <Row>
        <div>
          <strong>{facility.name}</strong>
          <div>
            <Muted>{facility.guest_subtitle}</Muted>
          </div>
          <div>
            <Muted>
              {facility.unit_price.formatted_text}
              {availabilitySuffix(facility, copy)}
            </Muted>
          </div>
        </div>
        <QuantityStepper
          value={quantity}
          onDecrease={() => props.onSetQuantity(payload, quantity - 1)}
          onIncrease={() => props.onSetQuantity(payload, quantity + 1)}
          disabledIncrease={soldOut}
        />
      </Row>
    </Card>
  );
};

// Nintendo: a time-slot booking (date + hour + console count). Keeps its own form and an explicit
// "Add to cart" button — each booking is its own cart line (re-adding the same slot updates it).
const HourlyCard = (props) => {
  const facility = props.facility;
  const copy = props.copy;
  const buildingCode = props.buildingCode;
  const [date, setDate] = React.useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [slots, setSlots] = React.useState([]);
  const [slotIndex, setSlotIndex] = React.useState("");
  const [consoleCount, setConsoleCount] = React.useState(1);
  const [duration, setDuration] = React.useState(1);
  const maxConsoles = facility.sub_capacity_max || 1;

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch(
          `/api/v1/add_on_facilities/${facility.id}/hourly_availability?date=${date}&building_code=${encodeURIComponent(buildingCode)}`,
          { headers: { Accept: "application/json" } },
        );
        const payload = await response.json();
        if (!active) return;
        setSlots(payload?.data?.slots || []);
        setSlotIndex("");
      } catch {
        if (active) setSlots([]);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [facility.id, date, buildingCode]);

  const startIdx = parseInt(slotIndex, 10);
  // Longest run of consecutive free hours from the chosen start (the Switch is exclusive, so any taken
  // hour ends the run), capped by the slot window's close (slots run 08:00–22:00).
  const maxDuration = React.useMemo(() => {
    if (Number.isNaN(startIdx) || !slots[startIdx]?.available) return 0;
    let count = 0;
    for (let i = startIdx; i < slots.length && slots[i]?.available; i++)
      count++;
    return count;
  }, [slots, startIdx]);

  const selected = slots[startIdx];
  // Clamp to the available run so changing the start hour (or a slot reload) can never leave the chosen
  // duration out of range — no reset effect needed.
  const effectiveDuration = Math.min(
    Math.max(duration, 1),
    Math.max(maxDuration, 1),
  );
  // Price mirrors the server AddOnFacility#variant_price: an explicit entry is a flat package for that
  // count; otherwise the single-console rate is charged PER console. × the number of booked hours.
  const hours = selected ? effectiveDuration : 0;
  const endSlot = selected
    ? slots[startIdx + effectiveDuration - 1]
    : undefined;
  const endHour = selected ? selected.hour + hours : 0;
  const rangeLabel = selected
    ? `${selected.label}–${String(endHour).padStart(2, "0")}:00`
    : "";
  const variant = facility.variant_prices?.[String(consoleCount)];
  const singleRate = Number(
    facility.variant_prices?.["1"] ?? facility.unit_price?.amount ?? 0,
  );
  const unitPrice =
    variant !== null && variant !== undefined
      ? Number(variant)
      : singleRate * consoleCount;
  const total = selected ? unitPrice * hours : 0;

  return (
    <Card>
      <div>
        <strong>{facility.name}</strong>
        <div>
          <Muted>{facility.guest_subtitle}</Muted>
        </div>
      </div>
      <Row>
        <label>
          <Muted>{copy.date} </Muted>
          <SmallInput
            as="input"
            type="date"
            style={{ width: "150px" }}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <label>
          <Muted>{copy.hour} </Muted>
          <Select
            value={slotIndex}
            onChange={(event) => setSlotIndex(event.target.value)}
          >
            <option value="">{copy.selectHour}</option>
            {slots.map((slot, index) => (
              <option
                key={slot.slot_start_at}
                value={index}
                disabled={!slot.available}
              >
                {slot.label}
                {slot.available ? "" : ` ${copy.taken}`}
              </option>
            ))}
          </Select>
        </label>
        <StepperField>
          <Muted>{copy.duration}</Muted>
          <QuantityStepper
            value={effectiveDuration}
            min={1}
            onDecrease={() =>
              setDuration(() => Math.max(1, effectiveDuration - 1))
            }
            onIncrease={() =>
              setDuration(() => Math.min(maxDuration, effectiveDuration + 1))
            }
            disabledIncrease={effectiveDuration >= maxDuration}
          />
        </StepperField>
        <StepperField>
          <Muted>{copy.consoles}</Muted>
          <QuantityStepper
            value={consoleCount}
            min={1}
            onDecrease={() =>
              setConsoleCount((current) => Math.max(1, current - 1))
            }
            onIncrease={() =>
              setConsoleCount((current) => Math.min(maxConsoles, current + 1))
            }
            disabledIncrease={consoleCount >= maxConsoles}
          />
        </StepperField>
      </Row>
      <SummaryLine>
        {selected ? (
          <>
            {consoleCount} {copy.consoleUnit(consoleCount)} × {hours}{" "}
            {copy.hourUnit(hours)} = <strong>{fmtRupiah(total)}</strong>
          </>
        ) : (
          <Muted>{copy.selectHourPrompt}</Muted>
        )}
      </SummaryLine>
      <Button
        type="button"
        disabled={!selected}
        onClick={() =>
          props.onAdd({
            key: `${facility.id}:${selected.slot_start_at}`,
            facilityId: facility.id,
            charge_group: facility.charge_group,
            facilityName: facility.name,
            dateLabel: `${date} ${rangeLabel}`,
            slotStartAt: selected.slot_start_at,
            slotEndAt: endSlot.slot_end_at,
            consoleCount,
            hours,
            unitPrice,
            quantity: 1,
            amount: total,
          })
        }
      >
        {selected ? `${copy.add} · ${fmtRupiah(total)}` : copy.add}
      </Button>
    </Card>
  );
};

const lineAmount = (line) => {
  if (line.charge_group === "charge_per_room_per_night") {
    return line.unit * line.quantity * line.nights;
  }
  if (line.charge_group === "charge_hourly") return line.amount;
  return line.unit * line.quantity;
};

const toItemPayload = (item) => {
  if (item.charge_group === "charge_hourly") {
    return {
      add_on_facility_id: item.facilityId,
      slot_start_at: item.slotStartAt,
      slot_end_at: item.slotEndAt,
      console_count: item.consoleCount,
    };
  }
  if (item.charge_group === "charge_per_room_per_night") {
    return {
      add_on_facility_id: item.facilityId,
      quantity: item.quantity,
      nights: item.nights,
    };
  }
  return { add_on_facility_id: item.facilityId, quantity: item.quantity };
};

// Built at render time (not stored) so cart rows re-translate when the guest toggles the language.
const itemLabel = (item, c) => {
  if (item.charge_group === "charge_hourly") {
    return `${item.facilityName} ${item.dateLabel} · ${item.consoleCount} ${c.consoleUnit(item.consoleCount)}`;
  }
  return `${item.facilityName} ×${item.quantity}`;
};

const itemBreakdown = (item, c) => {
  if (item.charge_group === "charge_hourly") {
    return `${item.consoleCount} ${c.consoleUnit(item.consoleCount)} · ${fmtRupiah(item.unitPrice)}/${c.hourUnit(1)} × ${item.hours} ${c.hourUnit(item.hours)}`;
  }
  if (item.charge_group === "charge_per_room_per_night") {
    return `${fmtRupiah(item.unit)} × ${item.quantity} ${c.bedUnit(item.quantity)} × ${item.nights} ${c.nightUnit(item.nights)}`;
  }
  return `${fmtRupiah(item.unit)} × ${item.quantity}`;
};

const AddOnFacilityPage = (props) => {
  const { building_code, room_no } = props;
  const { roomNumber, guestName, loadCachedProfile } = useRoom();
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;
  const [catalog, setCatalog] = React.useState(null);
  const [cart, setCart] = React.useState([]);
  const [cartOpen, setCartOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [roomModalOpen, setRoomModalOpen] = React.useState(false);
  const [guestEmail, setGuestEmail] = React.useState("");

  const effectiveRoom = roomNumber || room_no || null;
  // Derived (no effect): the popup opens automatically whenever no room is set yet, so the guest
  // keeps moving forward; it also opens on demand via the "Set room number" button.
  const showRoomModal = roomModalOpen || !effectiveRoom;

  React.useEffect(() => {
    loadCachedProfile(building_code);
  }, [building_code, loadCachedProfile]);

  React.useEffect(() => {
    if (!effectiveRoom) return undefined;
    let active = true;
    const load = async () => {
      try {
        const response = await fetch(
          `/api/v1/add_on_facilities/catalog?building_code=${encodeURIComponent(building_code)}&room_no=${encodeURIComponent(effectiveRoom)}`,
          { headers: { Accept: "application/json" } },
        );
        const payload = await response.json();
        if (active) setCatalog(payload.data);
      } catch {
        if (active) setError(c.loadError);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [building_code, effectiveRoom, c.loadError]);

  const getQuantity = (key) =>
    cart.find((line) => line.key === key)?.quantity || 0;

  // Quantity-type lines (extra bed, swimming ring): set the line to a quantity, adding it on the
  // first +, removing it at 0, and recomputing the price each time. Mirrors FNB updateCartQuantity.
  const setQuantity = (payload, nextQuantity) =>
    setCart((current) => {
      const index = current.findIndex((line) => line.key === payload.key);
      if (nextQuantity <= 0) {
        return current.filter((line) => line.key !== payload.key);
      }
      if (index < 0) {
        const line = { ...payload, quantity: nextQuantity };
        line.amount = lineAmount(line);
        return [...current, line];
      }
      return current.map((line, position) => {
        if (position !== index) return line;
        const updated = { ...line, quantity: nextQuantity };
        updated.amount = lineAmount(updated);
        return updated;
      });
    });

  // Nintendo bookings: keyed by slot, so re-adding the same slot replaces it rather than duplicating.
  const addBooking = (line) =>
    setCart((current) => {
      const index = current.findIndex((existing) => existing.key === line.key);
      if (index < 0) return [...current, line];
      return current.map((existing, position) =>
        position === index ? line : existing,
      );
    });

  const removeLine = (key) =>
    setCart((current) => current.filter((line) => line.key !== key));

  const cartTotal = cart.reduce(
    (sum, line) => sum + Number(line.amount || 0),
    0,
  );
  // The server prices the 7% per rental, rounded half up, and one cart line is one rental.
  const taxBps = Number(catalog?.tax?.rate_bps || 0);
  const cartTax = taxBps
    ? cart.reduce(
        (sum, line) =>
          sum +
          Math.floor(
            (Math.round(Number(line.amount || 0)) * taxBps + 5000) / 10000,
          ),
        0,
      )
    : 0;
  const cartPayable = cartTotal + cartTax;
  const cartCount = cart.reduce(
    (sum, line) => sum + Number(line.quantity || 1),
    0,
  );

  const checkout = async () => {
    if (!cart.length) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch("/api/v1/add_on_orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          building_code,
          room_no: effectiveRoom,
          guest_name: guestName || undefined,
          guest_email: guestEmail || undefined,
          items: cart.map(toItemPayload),
        }),
      });
      const payload = await response.json();
      if (response.ok && payload?.data?.payment_link_url) {
        saveOrderToLocalHistory({
          buildingCode: building_code,
          kind: "add_on",
          order: {
            id: payload.data.id,
            order_number: "Add-On Facility",
            created_at: payload.data.created_at,
          },
        });
        window.location.assign(payload.data.payment_link_url);
        return;
      }
      setError(payload?.message || c.createError);
      setSubmitting(false);
    } catch {
      setError(c.createError);
      setSubmitting(false);
    }
  };

  const facilities = catalog?.facilities || [];
  const extraBeds = facilities.filter(
    (facility) => facility.charge_group === "charge_per_room_per_night",
  );
  const hourly = facilities.filter(
    (facility) => facility.charge_group === "charge_hourly",
  );
  const oneTime = facilities.filter(
    (facility) => facility.charge_group === "charge_one_time",
  );

  return (
    <Page>
      <PageHeader
        title={c.title}
        backPath={`/hebe/${building_code}/hgs`}
        showGuestInfo
        buildingCode={building_code}
        maxWidth="720px"
      />
      <Container>
        <Subtitle>{c.subtitle}</Subtitle>

        {!effectiveRoom ? (
          <Card>
            {c.setRoomPrompt}
            <div style={{ marginTop: 10 }}>
              <Button type="button" onClick={() => setRoomModalOpen(true)}>
                {c.setRoomBtn}
              </Button>
            </div>
          </Card>
        ) : loading ? (
          <Card>{c.loading}</Card>
        ) : (
          <div>
            {error && !cartOpen ? <ErrorText>{error}</ErrorText> : null}

            {extraBeds.length ? (
              <GroupTitle>{c.groupExtraBed}</GroupTitle>
            ) : null}
            {extraBeds.map((facility) => (
              <ExtraBedCard
                key={facility.id}
                facility={facility}
                copy={c}
                getQuantity={getQuantity}
                onSetQuantity={setQuantity}
              />
            ))}

            {hourly.length ? <GroupTitle>{c.groupNintendo}</GroupTitle> : null}
            {hourly.map((facility) => (
              <HourlyCard
                key={facility.id}
                facility={facility}
                buildingCode={building_code}
                copy={c}
                onAdd={addBooking}
              />
            ))}

            {oneTime.length ? (
              <GroupTitle>{c.groupSwimmingRing}</GroupTitle>
            ) : null}
            {oneTime.map((facility) => (
              <QuantityCard
                key={facility.id}
                facility={facility}
                copy={c}
                getQuantity={getQuantity}
                onSetQuantity={setQuantity}
              />
            ))}

            {facilities.length === 0 ? <Card>{c.noneAvailable}</Card> : null}
          </div>
        )}
      </Container>

      {cart.length ? (
        <FloatingCartButton type="button" onClick={() => setCartOpen(true)}>
          <CartIconWrapper>
            <ShoppingCart size={20} />
            <CartBadge>{cartCount}</CartBadge>
          </CartIconWrapper>
          <span>
            {c.viewCart} • {fmtRupiah(cartPayable)}
          </span>
        </FloatingCartButton>
      ) : null}

      {cartOpen ? (
        <>
          <Overlay onClick={() => setCartOpen(false)} />
          <Drawer>
            <DrawerHeader>
              <div>
                <DrawerTitle>{c.cartTitle}</DrawerTitle>
                {effectiveRoom ? (
                  <Muted>
                    {guestName ? `${guestName} • ` : ""}
                    {c.deliverToRoom} {effectiveRoom}
                  </Muted>
                ) : null}
              </div>
              <CloseButton type="button" onClick={() => setCartOpen(false)}>
                <X size={20} />
              </CloseButton>
            </DrawerHeader>

            <DrawerBody>
              {cart.length === 0 ? (
                <Muted>{c.emptyCart}</Muted>
              ) : (
                cart.map((item) => (
                  <CartItem key={item.key}>
                    <div style={{ flex: 1 }}>
                      <CartItemLabel>{itemLabel(item, c)}</CartItemLabel>
                      <div>
                        <Muted>{itemBreakdown(item, c)}</Muted>
                      </div>
                      <CartItemPrice>
                        {fmtRupiah(item.amount || 0)}
                      </CartItemPrice>
                    </div>
                    <CartItemRight>
                      {item.charge_group === "charge_hourly" ? null : (
                        <QuantityStepper
                          value={item.quantity}
                          onDecrease={() =>
                            setQuantity(item, item.quantity - 1)
                          }
                          onIncrease={() =>
                            setQuantity(item, item.quantity + 1)
                          }
                        />
                      )}
                      <RemoveButton
                        type="button"
                        onClick={() => removeLine(item.key)}
                      >
                        {c.remove}
                      </RemoveButton>
                    </CartItemRight>
                  </CartItem>
                ))
              )}
            </DrawerBody>

            <DrawerFooter>
              {error ? <ErrorText>{error}</ErrorText> : null}
              <div>
                <Muted>{c.emailLabel}</Muted>
                <EmailInput
                  type="email"
                  value={guestEmail}
                  onChange={(event) => setGuestEmail(event.target.value)}
                  placeholder="you@email.com"
                />
              </div>
              <CartSummary>
                <span>
                  {c.deliverToRoom} <strong>{effectiveRoom}</strong>
                </span>
                {cartTax > 0 ? (
                  <span>
                    {catalog?.tax?.label} <strong>{fmtRupiah(cartTax)}</strong>
                  </span>
                ) : null}
                <CartTotal>
                  {c.total} <strong>{fmtRupiah(cartPayable)}</strong>
                </CartTotal>
              </CartSummary>
              <Button
                type="button"
                disabled={submitting || !cart.length}
                onClick={checkout}
                style={{ width: "100%" }}
              >
                {submitting ? c.processing : c.orderPay}
              </Button>
              <Muted>{c.xenditNote}</Muted>
            </DrawerFooter>
          </Drawer>
        </>
      ) : null}

      {showRoomModal ? (
        <RoomInputModal
          onRoomVerified={() => setRoomModalOpen(false)}
          buildingCode={building_code}
        />
      ) : null}
    </Page>
  );
};

export default AddOnFacilityPage;
